import React, { useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";

export const MAX_RANGE_DAYS = 20;

interface Props {
  availableDates: (string | Date)[];
  range: { start: string; end: string } | null;
  onApply: (range: { start: string; end: string }) => void;
}

// Inline calendar for a multi-day range. Only days the server has data for can
// be picked, and the end is capped at MAX_RANGE_DAYS from the start.
const RangePicker = ({ availableDates, range, onApply }: Props) => {
  const [start, setStart] = useState<Date | null>(range ? dayjs(range.start).toDate() : null);
  const [end, setEnd] = useState<Date | null>(range ? dayjs(range.end).toDate() : null);

  const include = useMemo(() => availableDates.map((d) => dayjs(d).toDate()), [availableDates]);
  const today = new Date();
  // While only the start is picked, the last pickable day is start + 19. An
  // earlier day stays clickable and simply becomes the new start.
  const maxDate =
    start && !end
      ? new Date(Math.min(today.getTime(), dayjs(start).add(MAX_RANGE_DAYS - 1, "day").toDate().getTime()))
      : today;

  const days = start && end ? dayjs(end).diff(dayjs(start), "day") + 1 : 0;

  return (
    <div style={{ background: "#fff", padding: 6 }}>
      <DatePicker
        inline
        selectsRange
        startDate={start || undefined}
        endDate={end || undefined}
        includeDates={include}
        maxDate={maxDate}
        onChange={(dates: [Date | null, Date | null]) => {
          const [s, e] = dates;
          setStart(s);
          setEnd(e);
        }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 4px 2px", fontSize: 12, color: "#4b5563" }}>
        <span style={{ marginRight: "auto" }}>
          {start && end
            ? `${dayjs(start).format("D MMM")} – ${dayjs(end).format("D MMM")} · ${days} day${days > 1 ? "s" : ""}`
            : start
              ? `Pick the last day (max ${MAX_RANGE_DAYS} days)`
              : "Pick the first day"}
        </span>
        <button
          type="button"
          disabled={!start || !end}
          onClick={() =>
            start && end && onApply({ start: dayjs(start).format("YYYY-MM-DD"), end: dayjs(end).format("YYYY-MM-DD") })
          }
          style={{
            height: 28,
            padding: "0 12px",
            borderRadius: 6,
            border: "none",
            background: start && end ? "#ED6926" : "#e5e7eb",
            color: start && end ? "#fff" : "#9ca3af",
            fontSize: 12,
            cursor: start && end ? "pointer" : "not-allowed",
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export default RangePicker;
